import { assertNever } from "@p0security/frontend-core";
import {
  type AgentRule,
  type AgenticActorRule,
  type AgenticRequestorRule,
  type AnyRule,
  ApprovalRule,
  type AutoApprover,
  Filter,
  type GroupRequestorRule,
  IntegrationResourceRule,
  RequestorRule,
  ResourceRule,
  type UserRequestorRule,
} from "@p0security/shared/types/policy/types";
import { cloneDeep, omit } from "lodash";

export const newResourceRuleOfType = (
  type: ResourceRule["type"]
): ResourceRule => {
  switch (type) {
    case "any":
      return { type };
    case "integration":
      return { type, service: "aws", accessType: "any", filters: {} };
    default:
      return assertNever(type);
  }
};

export const updateResourceService = (
  service: IntegrationResourceRule["service"],
  resource: ResourceRule
): ResourceRule => {
  if (resource.type !== "integration") {
    return resource;
  }
  return { type: "integration", service, accessType: "any", filters: {} };
};

export const updateResourceAccessType = <T extends IntegrationResourceRule>(
  accessType: T["accessType"],
  resource: T
): T => {
  if (!resource.service) return resource;
  return { ...resource, accessType, filters: {} };
};

export const newResourceRuleWithFilter = <T extends IntegrationResourceRule>(
  resource: T,
  filterKey: keyof T["filters"],
  filter: Filter,
  oldFilterKey?: keyof T["filters"]
): T => {
  const { service } = resource;
  if (!service) {
    return resource;
  }
  const currentFilters = cloneDeep(resource.filters) || {};
  const newFilters = {
    ...(oldFilterKey ? omit(currentFilters, oldFilterKey) : currentFilters),
    [filterKey]: filter,
  };
  return {
    ...resource,
    filters: newFilters,
  };
};

export const newResourceRuleWithoutFilter = <T extends IntegrationResourceRule>(
  resource: T,
  filterKey: string
): T => {
  const { service } = resource;
  if (!service) {
    return resource;
  }
  const currentFilters = resource.filters || {};
  return {
    ...resource,
    filters: omit(currentFilters, filterKey),
  };
};

export const newUserOrGroupRuleOfType = (
  type: AnyRule["type"] | GroupRequestorRule["type"] | UserRequestorRule["type"]
): AnyRule | GroupRequestorRule | UserRequestorRule => {
  switch (type) {
    case "any":
      return { type };
    case "user":
      return { type, uid: "" };
    case "group":
      return {
        type,
        groups: [{ id: "", label: "", directory: "okta" }],
        effect: "keep",
      };
    default:
      return assertNever(type);
  }
};

export function newRequestorRuleOfType<T extends RequestorRule["type"]>(
  type: T
): Extract<RequestorRule, { type: T }>;
export function newRequestorRuleOfType(
  type: RequestorRule["type"]
): RequestorRule {
  switch (type) {
    case "agentic":
      return { type, actor: { type: "any" }, subject: { type: "any" } };
    case "any":
      return { type };
    case "user":
    case "group":
      return newUserOrGroupRuleOfType(type);
    default:
      return assertNever(type);
  }
}

export function newAgenticSubjectRuleOfType<
  T extends AgenticRequestorRule["subject"]["type"],
>(type: T): Extract<AgenticRequestorRule["subject"], { type: T }>;
export function newAgenticSubjectRuleOfType(
  type: AgenticRequestorRule["subject"]["type"]
): AgenticRequestorRule["subject"] {
  switch (type) {
    case "any":
      return { type };
    case "user":
      return { type, user: newUserOrGroupRuleOfType("user") };
    case "agent":
      return { type, agent: { type: "any" } };
    default:
      return assertNever(type);
  }
}

export const newAgentRule = (type: AgentRule["type"]): AgentRule =>
  type === "any"
    ? { type }
    : type === "agent-client"
      ? { type: "agent-client", clientId: "" }
      : type === "agent-owner"
        ? { type, owner: "" }
        : type === "owner-group"
          ? { type, groups: { type: "group", effect: "keep", groups: [] } }
          : type === "provider"
            ? { type, providerId: "" }
            : assertNever(type);

export const newActorAgentRule = (
  type: AgenticActorRule["type"]
): AgenticActorRule =>
  type === "same-as-subject" ? { type } : newAgentRule(type);

export const newApprovalRuleOfType = (
  type: ApprovalRule["type"],
  installedAutoApprovers: AutoApprover[]
): ApprovalRule | undefined => {
  const [firstAuto] = installedAutoApprovers;
  switch (type) {
    case "persistent":
    case "deny":
      return { type };
    case "p0":
      return { type, options: {} };
    case "auto":
      return firstAuto && { type, integration: firstAuto, options: {} };
    case "group":
      return {
        type,
        groups: [{ id: "", label: "", directory: "okta" }],
        effect: "keep",
        options: {},
      };
    case "escalation":
      return firstAuto && { type, integration: firstAuto, services: [] };
    case "requestor-profile":
      return { type, directory: "okta", options: {} };
    default:
      return assertNever(type);
  }
};
